/**
 * Lower-Middle-Income Countries (LMIC) list
 * This list is based on the World Bank classification as of 2023.
 * It includes countries that are classified as lower-middle-income.
 * The list is used for various purposes, including economic analysis and development planning.
 * For latest information, see: https://datahelpdesk.worldbank.org/knowledgebase/articles/906519-world-bank-country-and-lending-groups
 */

/**
 * LMIC Countries with ISO codes and classification
 * Structure matches countries.js for reliable matching
 * Classification: LI = Low Income, LMI = Lower Middle Income
 */
export const LMIC_COUNTRIES = {
  AF: {
    name: "Afghanistan",
    iso3166_1_alpha2: "AF",
    iso3166_1_alpha3: "AFG",
    classification: "LI",
  },
  DZ: {
    name: "Algeria",
    iso3166_1_alpha2: "DZ",
    iso3166_1_alpha3: "DZA",
    classification: "LMI",
  },
  AO: {
    name: "Angola",
    iso3166_1_alpha2: "AO",
    iso3166_1_alpha3: "AGO",
    classification: "LMI",
  },
  BD: {
    name: "Bangladesh",
    iso3166_1_alpha2: "BD",
    iso3166_1_alpha3: "BGD",
    classification: "LMI",
  },
  BJ: {
    name: "Benin",
    iso3166_1_alpha2: "BJ",
    iso3166_1_alpha3: "BEN",
    classification: "LMI",
  },
  BT: {
    name: "Bhutan",
    iso3166_1_alpha2: "BT",
    iso3166_1_alpha3: "BTN",
    classification: "LMI",
  },
  BO: {
    name: "Bolivia",
    iso3166_1_alpha2: "BO",
    iso3166_1_alpha3: "BOL",
    classification: "LMI",
  },
  BF: {
    name: "Burkina Faso",
    iso3166_1_alpha2: "BF",
    iso3166_1_alpha3: "BFA",
    classification: "LI",
  },
  BI: {
    name: "Burundi",
    iso3166_1_alpha2: "BI",
    iso3166_1_alpha3: "BDI",
    classification: "LI",
  },
  KH: {
    name: "Cambodia",
    iso3166_1_alpha2: "KH",
    iso3166_1_alpha3: "KHM",
    classification: "LMI",
  },
  CM: {
    name: "Cameroon",
    iso3166_1_alpha2: "CM",
    iso3166_1_alpha3: "CMR",
    classification: "LMI",
  },
  CV: {
    name: "Cape Verde",
    iso3166_1_alpha2: "CV",
    iso3166_1_alpha3: "CPV",
    classification: "LMI",
  },
  CF: {
    name: "Central African Republic",
    iso3166_1_alpha2: "CF",
    iso3166_1_alpha3: "CAF",
    classification: "LI",
  },
  TD: {
    name: "Chad",
    iso3166_1_alpha2: "TD",
    iso3166_1_alpha3: "TCD",
    classification: "LI",
  },
  KM: {
    name: "Comoros",
    iso3166_1_alpha2: "KM",
    iso3166_1_alpha3: "COM",
    classification: "LMI",
  },
  CG: {
    name: "Republic of the Congo",
    iso3166_1_alpha2: "CG",
    iso3166_1_alpha3: "COG",
    classification: "LMI",
  },
  CD: {
    name: "Democratic Republic of Congo",
    iso3166_1_alpha2: "CD",
    iso3166_1_alpha3: "COD",
    classification: "LI",
  },
  CI: {
    name: "Côte d'Ivoire",
    iso3166_1_alpha2: "CI",
    iso3166_1_alpha3: "CIV",
    classification: "LMI",
  },
  DJ: {
    name: "Djibouti",
    iso3166_1_alpha2: "DJ",
    iso3166_1_alpha3: "DJI",
    classification: "LMI",
  },
  EG: {
    name: "Egypt",
    iso3166_1_alpha2: "EG",
    iso3166_1_alpha3: "EGY",
    classification: "LMI",
  },
  SV: {
    name: "El Salvador",
    iso3166_1_alpha2: "SV",
    iso3166_1_alpha3: "SLV",
    classification: "LMI",
  },
  ER: {
    name: "Eritrea",
    iso3166_1_alpha2: "ER",
    iso3166_1_alpha3: "ERI",
    classification: "LI",
  },
  SZ: {
    name: "Eswatini",
    iso3166_1_alpha2: "SZ",
    iso3166_1_alpha3: "SWZ",
    classification: "LMI",
  },
  ET: {
    name: "Ethiopia",
    iso3166_1_alpha2: "ET",
    iso3166_1_alpha3: "ETH",
    classification: "LI",
  },
  GM: {
    name: "Gambia",
    iso3166_1_alpha2: "GM",
    iso3166_1_alpha3: "GMB",
    classification: "LI",
  },
  GH: {
    name: "Ghana",
    iso3166_1_alpha2: "GH",
    iso3166_1_alpha3: "GHA",
    classification: "LMI",
  },
  GW: {
    name: "Guinea-Bissau",
    iso3166_1_alpha2: "GW",
    iso3166_1_alpha3: "GNB",
    classification: "LI",
  },
  HN: {
    name: "Honduras",
    iso3166_1_alpha2: "HN",
    iso3166_1_alpha3: "HND",
    classification: "LMI",
  },
  IN: {
    name: "India",
    iso3166_1_alpha2: "IN",
    iso3166_1_alpha3: "IND",
    classification: "LMI",
  },
  ID: {
    name: "Indonesia",
    iso3166_1_alpha2: "ID",
    iso3166_1_alpha3: "IDN",
    classification: "LMI",
  },
  IR: {
    name: "Iran",
    iso3166_1_alpha2: "IR",
    iso3166_1_alpha3: "IRN",
    classification: "LMI",
  },
  JO: {
    name: "Jordan",
    iso3166_1_alpha2: "JO",
    iso3166_1_alpha3: "JOR",
    classification: "LMI",
  },
  KE: {
    name: "Kenya",
    iso3166_1_alpha2: "KE",
    iso3166_1_alpha3: "KEN",
    classification: "LMI",
  },
  KI: {
    name: "Kiribati",
    iso3166_1_alpha2: "KI",
    iso3166_1_alpha3: "KIR",
    classification: "LMI",
  },
  KP: {
    name: "North Korea",
    iso3166_1_alpha2: "KP",
    iso3166_1_alpha3: "PRK",
    classification: "LI",
  },
  KG: {
    name: "Kyrgyzstan",
    iso3166_1_alpha2: "KG",
    iso3166_1_alpha3: "KGZ",
    classification: "LMI",
  },
  LA: {
    name: "Laos",
    iso3166_1_alpha2: "LA",
    iso3166_1_alpha3: "LAO",
    classification: "LMI",
  },
  LB: {
    name: "Lebanon",
    iso3166_1_alpha2: "LB",
    iso3166_1_alpha3: "LBN",
    classification: "LMI",
  },
  LS: {
    name: "Lesotho",
    iso3166_1_alpha2: "LS",
    iso3166_1_alpha3: "LSO",
    classification: "LMI",
  },
  LR: {
    name: "Liberia",
    iso3166_1_alpha2: "LR",
    iso3166_1_alpha3: "LBR",
    classification: "LI",
  },
  MG: {
    name: "Madagascar",
    iso3166_1_alpha2: "MG",
    iso3166_1_alpha3: "MDG",
    classification: "LI",
  },
  MW: {
    name: "Malawi",
    iso3166_1_alpha2: "MW",
    iso3166_1_alpha3: "MWI",
    classification: "LI",
  },
  ML: {
    name: "Mali",
    iso3166_1_alpha2: "ML",
    iso3166_1_alpha3: "MLI",
    classification: "LI",
  },
  MR: {
    name: "Mauritania",
    iso3166_1_alpha2: "MR",
    iso3166_1_alpha3: "MRT",
    classification: "LMI",
  },
  FM: {
    name: "Micronesia",
    iso3166_1_alpha2: "FM",
    iso3166_1_alpha3: "FSM",
    classification: "LMI",
  },
  MN: {
    name: "Mongolia",
    iso3166_1_alpha2: "MN",
    iso3166_1_alpha3: "MNG",
    classification: "LMI",
  },
  MA: {
    name: "Morocco",
    iso3166_1_alpha2: "MA",
    iso3166_1_alpha3: "MAR",
    classification: "LMI",
  },
  MZ: {
    name: "Mozambique",
    iso3166_1_alpha2: "MZ",
    iso3166_1_alpha3: "MOZ",
    classification: "LI",
  },
  MM: {
    name: "Myanmar",
    iso3166_1_alpha2: "MM",
    iso3166_1_alpha3: "MMR",
    classification: "LMI",
  },
  NP: {
    name: "Nepal",
    iso3166_1_alpha2: "NP",
    iso3166_1_alpha3: "NPL",
    classification: "LMI",
  },
  NI: {
    name: "Nicaragua",
    iso3166_1_alpha2: "NI",
    iso3166_1_alpha3: "NIC",
    classification: "LMI",
  },
  NE: {
    name: "Niger",
    iso3166_1_alpha2: "NE",
    iso3166_1_alpha3: "NER",
    classification: "LI",
  },
  NG: {
    name: "Nigeria",
    iso3166_1_alpha2: "NG",
    iso3166_1_alpha3: "NGA",
    classification: "LMI",
  },
  PK: {
    name: "Pakistan",
    iso3166_1_alpha2: "PK",
    iso3166_1_alpha3: "PAK",
    classification: "LMI",
  },
  PG: {
    name: "Papua New Guinea",
    iso3166_1_alpha2: "PG",
    iso3166_1_alpha3: "PNG",
    classification: "LMI",
  },
  PH: {
    name: "Philippines",
    iso3166_1_alpha2: "PH",
    iso3166_1_alpha3: "PHL",
    classification: "LMI",
  },
  RW: {
    name: "Rwanda",
    iso3166_1_alpha2: "RW",
    iso3166_1_alpha3: "RWA",
    classification: "LI",
  },
  ST: {
    name: "São Tomé and Príncipe",
    iso3166_1_alpha2: "ST",
    iso3166_1_alpha3: "STP",
    classification: "LMI",
  },
  SN: {
    name: "Senegal",
    iso3166_1_alpha2: "SN",
    iso3166_1_alpha3: "SEN",
    classification: "LMI",
  },
  SL: {
    name: "Sierra Leone",
    iso3166_1_alpha2: "SL",
    iso3166_1_alpha3: "SLE",
    classification: "LI",
  },
  SB: {
    name: "Solomon Islands",
    iso3166_1_alpha2: "SB",
    iso3166_1_alpha3: "SLB",
    classification: "LMI",
  },
  SO: {
    name: "Somalia",
    iso3166_1_alpha2: "SO",
    iso3166_1_alpha3: "SOM",
    classification: "LI",
  },
  SS: {
    name: "South Sudan",
    iso3166_1_alpha2: "SS",
    iso3166_1_alpha3: "SSD",
    classification: "LI",
  },
  LK: {
    name: "Sri Lanka",
    iso3166_1_alpha2: "LK",
    iso3166_1_alpha3: "LKA",
    classification: "LMI",
  },
  SD: {
    name: "Sudan",
    iso3166_1_alpha2: "SD",
    iso3166_1_alpha3: "SDN",
    classification: "LI",
  },
  SY: {
    name: "Syria",
    iso3166_1_alpha2: "SY",
    iso3166_1_alpha3: "SYR",
    classification: "LI",
  },
  TJ: {
    name: "Tajikistan",
    iso3166_1_alpha2: "TJ",
    iso3166_1_alpha3: "TJK",
    classification: "LMI",
  },
  TZ: {
    name: "Tanzania",
    iso3166_1_alpha2: "TZ",
    iso3166_1_alpha3: "TZA",
    classification: "LMI",
  },
  TL: {
    name: "Timor-Leste",
    iso3166_1_alpha2: "TL",
    iso3166_1_alpha3: "TLS",
    classification: "LMI",
  },
  TG: {
    name: "Togo",
    iso3166_1_alpha2: "TG",
    iso3166_1_alpha3: "TGO",
    classification: "LI",
  },
  TN: {
    name: "Tunisia",
    iso3166_1_alpha2: "TN",
    iso3166_1_alpha3: "TUN",
    classification: "LMI",
  },
  UG: {
    name: "Uganda",
    iso3166_1_alpha2: "UG",
    iso3166_1_alpha3: "UGA",
    classification: "LI",
  },
  UA: {
    name: "Ukraine",
    iso3166_1_alpha2: "UA",
    iso3166_1_alpha3: "UKR",
    classification: "LMI",
  },
  UZ: {
    name: "Uzbekistan",
    iso3166_1_alpha2: "UZ",
    iso3166_1_alpha3: "UZB",
    classification: "LMI",
  },
  VU: {
    name: "Vanuatu",
    iso3166_1_alpha2: "VU",
    iso3166_1_alpha3: "VUT",
    classification: "LMI",
  },
  VN: {
    name: "Vietnam",
    iso3166_1_alpha2: "VN",
    iso3166_1_alpha3: "VNM",
    classification: "LMI",
  },
  PS: {
    name: "West Bank and Gaza",
    iso3166_1_alpha2: "PS",
    iso3166_1_alpha3: "PSE",
    classification: "LMI",
  },
  YE: {
    name: "Yemen",
    iso3166_1_alpha2: "YE",
    iso3166_1_alpha3: "YEM",
    classification: "LI",
  },
  ZM: {
    name: "Zambia",
    iso3166_1_alpha2: "ZM",
    iso3166_1_alpha3: "ZMB",
    classification: "LMI",
  },
  ZW: {
    name: "Zimbabwe",
    iso3166_1_alpha2: "ZW",
    iso3166_1_alpha3: "ZWE",
    classification: "LMI",
  },
};

/**
 * Checks if a country is classified as LMIC (Low or Lower-Middle Income)
 * @param {string} countryCode - The country ISO alpha-2 or alpha-3 code, or country name
 * @returns {boolean} - True if the country is LMIC, false otherwise
 */
export function isLMICCountry(countryCode) {
  if (!countryCode || typeof countryCode !== "string") return false;

  const trimmed = countryCode.trim();

  // Check by ISO alpha-2 code first (most common)
  if (LMIC_COUNTRIES[trimmed]) {
    return true;
  }

  // Check by ISO alpha-3 code
  const foundByAlpha3 = Object.values(LMIC_COUNTRIES).find(
    (country) => country.iso3166_1_alpha3 === trimmed,
  );
  if (foundByAlpha3) {
    return true;
  }

  // Fallback: check by country name
  const foundByName = Object.values(LMIC_COUNTRIES).find(
    (country) => country.name === trimmed,
  );
  return !!foundByName;
}

/**
 * Gets the suggested LMIC selection based on country
 * @param {string} countryCode - The country ISO alpha-2 or alpha-3 code, or country name
 * @returns {'Yes'|'No'|null} - Suggested selection or null if country not provided
 */
export function getSuggestedLMICSelection(countryCode) {
  if (!countryCode || typeof countryCode !== "string") return null;

  return isLMICCountry(countryCode) ? "Yes" : "No";
}

/**
 * Validates LMIC selection against country
 * @param {string} countryCode - The country ISO alpha-2 or alpha-3 code, or country name
 * @param {string} lmicSelection - The LMIC selection ('Yes' or 'No')
 * @returns {Object} - Validation result with isValid, warning, and suggestion
 */
export function validateLMICSelection(countryCode, lmicSelection) {
  if (!countryCode || !lmicSelection) {
    return { isValid: true, warning: null, suggestion: null };
  }

  const isCountryLMIC = isLMICCountry(countryCode);
  const suggestedSelection = getSuggestedLMICSelection(countryCode);

  // Get country name for display in warnings
  let countryName = countryCode;
  const countryData =
    LMIC_COUNTRIES[countryCode] ||
    Object.values(LMIC_COUNTRIES).find(
      (country) =>
        country.iso3166_1_alpha3 === countryCode ||
        country.name === countryCode,
    );
  if (countryData) {
    countryName = countryData.name;
  }

  // If selection matches the country classification, all good
  if (lmicSelection === suggestedSelection) {
    return { isValid: true, warning: null, suggestion: null };
  }

  // If user selected "Yes" but country is not LMIC
  if (lmicSelection === "Yes" && !isCountryLMIC) {
    return {
      isValid: true, // Allow to continue but show warning
      warning: `${countryName} is not classified as a Low or Middle-Income Country by the World Bank. Please verify your selection is correct.`,
      suggestion: "No",
    };
  }

  // If user selected "No" but country is LMIC
  if (lmicSelection === "No" && isCountryLMIC) {
    return {
      isValid: true, // Allow to continue but show warning
      warning: `${countryName} is classified as a Low or Middle-Income Country by the World Bank. You may be eligible for reduced membership rates.`,
      suggestion: "Yes",
    };
  }

  return { isValid: true, warning: null, suggestion: null };
}

/**
 * Gets information about a country's income classification
 * @param {string} countryCode - The country ISO alpha-2 or alpha-3 code, or country name
 * @returns {Object} - Country classification information
 */
export function getCountryIncomeInfo(countryCode) {
  if (!countryCode || typeof countryCode !== "string") {
    return {
      classification: "Unknown",
      isLMIC: false,
      description: "Country not found",
    };
  }

  const trimmed = countryCode.trim();

  // Find country data by ISO alpha-2 code first
  let countryData = LMIC_COUNTRIES[trimmed];

  // If not found, try ISO alpha-3 code
  if (!countryData) {
    countryData = Object.values(LMIC_COUNTRIES).find(
      (country) => country.iso3166_1_alpha3 === trimmed,
    );
  }

  // If not found, try country name
  if (!countryData) {
    countryData = Object.values(LMIC_COUNTRIES).find(
      (country) => country.name === trimmed,
    );
  }

  if (countryData) {
    if (countryData.classification === "LI") {
      return {
        classification: "Low Income",
        isLMIC: true,
        description: "GNI per capita of $1,085 or less (2022)",
      };
    } else if (countryData.classification === "LMI") {
      return {
        classification: "Lower Middle Income",
        isLMIC: true,
        description: "GNI per capita of $1,086 to $4,255 (2022)",
      };
    }
  }

  return {
    classification: "Upper Middle Income or High Income",
    isLMIC: false,
    description: "GNI per capita above $4,255 (2022)",
  };
}
